import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Wrench, 
  ArrowLeft,
  FileText,
  DollarSign,
  Clock,
  Package,
  CreditCard,
  Printer,
  CheckCircle,
  Camera,
  Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoicePage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [repairPhotos, setRepairPhotos] = useState({ before_photos: [], after_photos: [] });

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoiceId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setInvoice(response.data);
      
      // Fetch repair photos if booking_id exists
      if (response.data.booking_id) {
        fetchRepairPhotos(response.data.booking_id);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairPhotos = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/${bookingId}/photos`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRepairPhotos(response.data);
    } catch (error) {
      // Photos not available, that's ok
    }
  };

  const handlePayment = async () => {
    setPaying(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          invoice_id: invoiceId,
          origin_url: window.location.origin
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setPaying(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      paid: 'bg-green-100 text-green-800 border-green-200'
    };
    return (
      <Badge className={`${statusClasses[status] || 'bg-gray-100'} border font-medium`}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <FileText className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Invoice not found</h2>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <Card className="bg-white border border-slate-200" data-testid="invoice-card">
          {/* Invoice Header */}
          <CardHeader className="border-b border-slate-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500 tracking-widest uppercase mb-1">INVOICE</p>
                <CardTitle className="font-['JetBrains_Mono'] text-xl">
                  #{invoice.invoice_id}
                </CardTitle>
              </div>
              <div className="flex items-center gap-4">
                {getStatusBadge(invoice.status)}
                {invoice.status === 'paid' && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Paid</span>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {/* Invoice Details */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <p className="text-sm text-slate-500 mb-1">Date Created</p>
                <p className="font-medium text-slate-900">
                  {new Date(invoice.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Booking ID</p>
                <p className="font-['JetBrains_Mono'] text-slate-900">{invoice.booking_id}</p>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Labor Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-slate-400" />
                <h3 className="font-['Barlow'] text-lg font-bold text-slate-900">Labor</h3>
              </div>
              <div className="bg-slate-50 rounded-md p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">Work Hours</p>
                    <p className="text-sm text-slate-600">{invoice.labor_hours} hours × ${invoice.labor_rate}/hr</p>
                  </div>
                  <p className="font-bold text-lg text-slate-900">${invoice.labor_total?.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Parts Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-slate-400" />
                <h3 className="font-['Barlow'] text-lg font-bold text-slate-900">Parts</h3>
              </div>
              {invoice.parts && invoice.parts.length > 0 ? (
                <div className="bg-slate-50 rounded-md overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider p-3">Part</th>
                        <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider p-3">Qty</th>
                        <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider p-3">Price</th>
                        <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider p-3">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {invoice.parts.map((part, idx) => {
                        const unitPrice = part.unit_price || part.price || 0;
                        return (
                          <tr key={idx}>
                            <td className="p-3 text-slate-900">{part.name}</td>
                            <td className="p-3 text-center text-slate-600">{part.quantity}</td>
                            <td className="p-3 text-right text-slate-600">${unitPrice.toFixed(2)}</td>
                            <td className="p-3 text-right font-medium text-slate-900">
                              ${(part.quantity * unitPrice).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-md p-4 text-center text-slate-500">
                  No parts added
                </div>
              )}
              <div className="flex justify-between items-center mt-3 px-4">
                <p className="text-slate-600">Parts Subtotal</p>
                <p className="font-bold text-slate-900">${invoice.parts_total?.toFixed(2)}</p>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Totals */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-slate-600">Subtotal</p>
                <p className="font-medium text-slate-900">${invoice.subtotal?.toFixed(2)}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-slate-600">Tax (8%)</p>
                <p className="font-medium text-slate-900">${invoice.tax?.toFixed(2)}</p>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <p className="font-['Barlow'] text-xl font-bold text-slate-900">Total Due</p>
                <p className="font-['Barlow'] text-2xl font-bold text-orange-600">${invoice.total?.toFixed(2)}</p>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mt-6 p-4 bg-slate-50 rounded-md">
                <p className="text-sm text-slate-500 mb-1">Notes</p>
                <p className="text-slate-700">{invoice.notes}</p>
              </div>
            )}

            {/* Repair Photos */}
            {(repairPhotos.before_photos.length > 0 || repairPhotos.after_photos.length > 0) && (
              <div className="mt-8 p-6 bg-slate-50 rounded-lg">
                <h3 className="font-['Barlow'] text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-orange-500" />
                  Repair Photos
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Before Photos */}
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      Before Repair
                    </p>
                    {repairPhotos.before_photos.length === 0 ? (
                      <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <Image className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                        <p className="text-xs text-slate-400">No photos</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {repairPhotos.before_photos.map((photo) => (
                          <img 
                            key={photo.photo_id}
                            src={`${API_URL}${photo.url}`} 
                            alt="Before repair" 
                            className="w-full h-20 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90"
                            onClick={() => window.open(`${API_URL}${photo.url}`, '_blank')}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* After Photos */}
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      After Repair
                    </p>
                    {repairPhotos.after_photos.length === 0 ? (
                      <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <Image className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                        <p className="text-xs text-slate-400">No photos</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {repairPhotos.after_photos.map((photo) => (
                          <img 
                            key={photo.photo_id}
                            src={`${API_URL}${photo.url}`} 
                            alt="After repair" 
                            className="w-full h-20 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90"
                            onClick={() => window.open(`${API_URL}${photo.url}`, '_blank')}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Button */}
            {invoice.status === 'sent' && invoice.customer_id === user?.user_id && (
              <div className="mt-8">
                <Button 
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                  onClick={handlePayment}
                  disabled={paying}
                  data-testid="pay-invoice-btn"
                >
                  {paying ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ${invoice.total?.toFixed(2)}
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Print Button */}
            <div className="mt-4 flex justify-center">
              <Button 
                variant="outline"
                onClick={() => window.print()}
                className="text-slate-600"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Invoice
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InvoicePage;
